import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Building2, Plus, X } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api, errorMessage } from "@/lib/api";
import { useDebounced } from "@/hooks/useDirectory";
import { slugify } from "@/lib/format";
import { cn } from "@/lib/utils";

function CreateOrgDialog({ open, onOpenChange, initialName, onCreated }) {
  const [form, setForm] = useState({ name: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setForm({ name: initialName }); }, [open, initialName]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async () => {
    if (!form.name.trim()) return toast.error("Le nom est obligatoire.");
    setSaving(true);
    try {
      const { data } = await api.post("/organizations", form);
      toast.success(`Organisation « ${data.name} » créée`);
      onCreated({ id: data.id, name: data.name });
      onOpenChange(false);
    } catch (error) {
      const existing = error.response?.data?.detail?.existing;
      if (existing) {
        toast.info("Cette organisation existe déjà : elle a été sélectionnée.");
        onCreated(existing);
        onOpenChange(false);
      } else toast.error(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="create-org-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Créer une entreprise</DialogTitle>
          <DialogDescription>Elle sera ajoutée dans Maisonna. Vous pourrez la compléter plus tard.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {[["name", "Nom *"]].map(([key, label]) => (
            <div key={key} className="space-y-1.5">
              <Label htmlFor={`create-org-${key}`}>{label}</Label>
              <Input id={`create-org-${key}`} value={form[key]} onChange={set(key)} data-testid={`create-org-${key}-input`} />
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={submit} disabled={saving} className="rounded-full" data-testid="create-org-submit-button">
            {saving ? "Création…" : "Créer l'entreprise"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function OrgPicker({ value, onChange, invalid }) {
  const [text, setText] = useState("");
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const wrapper = useRef(null);
  const q = useDebounced(text, 200);
  const { data = [], isFetching } = useQuery({
    queryKey: ["org-autocomplete", q],
    queryFn: async () => (await api.get(`/organizations/autocomplete?q=${encodeURIComponent(q)}&limit=8`)).data,
    enabled: q.trim().length > 0,
  });
  useEffect(() => {
    const close = (e) => wrapper.current && !wrapper.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  const add = (org) => {
    if (!value.some((v) => v.id === org.id)) onChange([...value, { id: org.id, name: org.name }]);
    setText("");
    setOpen(false);
  };
  const exact = data.some((o) => o.name.toLowerCase() === text.trim().toLowerCase());

  return (
    <div ref={wrapper} className="relative">
      {value.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-2">
          {value.map((org) => (
            <span key={org.id} data-testid={`org-picker-selected-${slugify(org.name)}`} className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 py-1 pl-3 pr-1.5 text-sm font-semibold text-emerald-800">
              <Building2 className="h-3.5 w-3.5" /> {org.name}
              <button type="button" onClick={() => onChange(value.filter((v) => v.id !== org.id))} className="rounded-full p-0.5 hover:bg-emerald-100" aria-label="Retirer">
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      <Input
        value={text}
        onChange={(e) => { setText(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder={value.length ? "Ajouter une autre organisation…" : "Tapez le nom de l'entreprise (ex : Airb…)"}
        className={cn(invalid && "border-red-400 focus-visible:ring-red-300")}
        data-testid="person-form-organization-input"
      />
      {open && text.trim() && (
        <div className="absolute z-30 mt-1.5 w-full overflow-hidden rounded-xl border border-border bg-white shadow-xl" data-testid="org-picker-dropdown">
          {data.map((org) => (
            <button type="button" key={org.id} onClick={() => add(org)} data-testid={`org-picker-option-${slugify(org.name)}`}
              className="flex w-full items-center gap-2 px-3.5 py-2.5 text-left text-sm hover:bg-stone-50">
              <Building2 className="h-4 w-4 text-stone-400" /><span className="font-medium">{org.name}</span>
              {org.city && <span className="ml-auto text-xs text-muted-foreground">{org.city}</span>}
            </button>
          ))}
          {!isFetching && !data.length && <div className="px-3.5 py-2.5 text-sm text-muted-foreground">Aucune entreprise trouvée.</div>}
          {!exact && (
            <button type="button" onClick={() => { setCreating(true); setOpen(false); }} data-testid="org-picker-create-button"
              className="flex w-full items-center gap-2 border-t border-border bg-accent/50 px-3.5 py-2.5 text-left text-sm font-semibold text-primary hover:bg-accent">
              <Plus className="h-4 w-4" /> Créer cette entreprise « {text.trim()} »
            </button>
          )}
        </div>
      )}
      <CreateOrgDialog open={creating} onOpenChange={setCreating} initialName={text.trim()} onCreated={add} />
    </div>
  );
}
